import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { BaseSheet } from "@/components/common/BaseSheet";
import { useIsMobile } from "@/hooks/ui/useIsMobile";
import { cn } from "@/lib/utils";
import SummaryCaptureForm from "@/components/voice-genie/SummaryCapture/SummaryCaptureForm";
import { useSummaryCapture } from "@/hooks/voice/useSummaryCapture";
import { trackCallClick } from "@/lib/ga4";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface VoiceTroubleSheetProps {
  open: boolean;
  onDismiss: () => void;
  brandColor: string;
  secondaryColor?: string | null;
  agentId?: string;
  agentUserId?: string;
  /** The tenant's own contact number, from genie config. Hidden entirely when absent. */
  phoneNumber?: string | null;
  onSwitchToText: () => void;
  onSummaryCaptured?: (email: string) => void;
  hideSummaryCapture?: boolean;
  onEmailFollowUp?: () => void;
}

/**
 * A lightweight, always-available "Having trouble?" fallback for the voice
 * surface. Offered manually (a subtle trigger, always present during a call)
 * or automatically when cheap frontend signals suggest the call is
 * struggling (see useVoiceTroubleSignals) — never from ML/quality scoring.
 * A rescued wobble is a captured lead: the email path reuses the same
 * get-a-summary pipeline as every other capture moment on this page.
 */
export const VoiceTroubleSheet = ({
  open,
  onDismiss,
  brandColor,
  secondaryColor,
  agentId,
  agentUserId,
  phoneNumber,
  onSwitchToText,
  onSummaryCaptured,
  hideSummaryCapture = false,
  onEmailFollowUp,
}: VoiceTroubleSheetProps) => {
  const isMobile = useIsMobile(768);
  const end = secondaryColor || "#FF4DB8";

  const capture = useSummaryCapture({
    agentId,
    agentUserId,
    source: "voice_trouble",
    onCaptured: onSummaryCaptured,
  });

  useEffect(() => {
    if (open && agentId && !hideSummaryCapture) capture.markShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId, hideSummaryCapture]);

  const telHref = phoneNumber ? `tel:${phoneNumber.replace(/[^\d+]/g, "")}` : null;

  return (
    <BaseSheet
      isOpen={open}
      onClose={onDismiss}
      title="Having trouble?"
      description="If voice isn't working well for you right now, here are a few other ways to get what you need."
      side={isMobile ? "bottom" : "right"}
      size={isMobile ? "full" : "md"}
      showCloseButton
      preventOutsideClick={false}
      overlayClassName="z-[11990]"
      contentClassName={cn("z-[12000] [&_button]:min-h-[44px] [&_button]:min-w-[44px]", isMobile ? "max-h-[90dvh] rounded-t-2xl" : "sm:max-w-md")}
      footer={
        <div className="flex w-full flex-col gap-2 pb-[max(8px,env(safe-area-inset-bottom))]">
          <Button
            type="button"
            className="w-full border-0 text-white shadow-sm"
            style={{ background: `linear-gradient(90deg, ${brandColor}, ${end})` }}
            onClick={() => {
              onSwitchToText();
              onDismiss();
            }}
          >
            Switch to typing
          </Button>
          {telHref && (
            <Button asChild type="button" variant="outline" className="w-full border-[#E5EAF0]">
              <a href={telHref} onClick={() => trackCallClick("voice_trouble_sheet")}>
                <GenieIcon name="phone" size={15} color={brandColor} className="mr-1.5" />
                 Call the Genie · {phoneNumber}
              </a>
            </Button>
          )}
          <Button type="button" variant="ghost" className="w-full" onClick={onDismiss}>
            Keep trying with voice
          </Button>
        </div>
      }
    >
      {onEmailFollowUp ? (
        <Button variant="outline" onClick={() => { onDismiss(); onEmailFollowUp(); }}>Continue over email</Button>
      ) : agentId && !hideSummaryCapture ? (
        <div className="rounded-2xl border border-[#E9EDF3] bg-white p-4">
          <SummaryCaptureForm
            brandColor={brandColor}
            secondaryColor={end}
            values={capture.values}
            onChange={capture.onChange}
            expanded={capture.expanded}
            onToggleExpanded={capture.toggleExpanded}
            onSubmit={capture.submit}
            isSubmitting={capture.isSubmitting}
            submitted={capture.submitted}
            error={capture.error}
            hideExpandToggle
            confirmationTitle="✓ On its way"
            confirmationBody="We'll email you the details from this conversation instead."
            alreadyCapturedEmail={capture.alreadyCaptured && !capture.editing ? capture.capturedEmail : null}
            onChangeEmail={capture.startEdit}
          />
        </div>
      ) : (
        <p className="text-sm leading-relaxed text-[#7C8B9C]" role="note">
          You can keep talking, switch to typing, or {telHref ? "give us a call" : "try again in a moment"} —
          whichever's easiest right now.
        </p>
      )}
    </BaseSheet>
  );
};

export default VoiceTroubleSheet;
